import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { CATEGORIES, catLabel, t } from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Sparkle } from "@phosphor-icons/react";

export default function SubmitPage() {
  const { lang, sourceLang } = useApp();
  const [collection, setCollection] = useState(sourceLang);
  const [form, setForm] = useState({
    word: "",
    category: "insult",
    region: "",
    definition: "",
    example: "",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [dupStatus, setDupStatus] = useState("idle"); // idle | checking | duplicate | ok

  function update(k, v) {
    setForm({ ...form, [k]: v });
  }

  useEffect(() => {
    const w = form.word.trim();
    if (!w) {
      setDupStatus("idle");
      return;
    }
    setDupStatus("checking");
    let ignore = false;
    const timer = setTimeout(async () => {
      try {
        const r = await api.get("/words/check", {
          params: { word: w, source_lang: collection },
        });
        if (!ignore) setDupStatus(r.data.exists ? "duplicate" : "ok");
      } catch {
        if (!ignore) setDupStatus("idle");
      }
    }, 500);
    return () => {
      ignore = true;
      clearTimeout(timer);
    };
  }, [form.word, collection]);

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      // For the Spanish collection the headword + definition are in Spanish.
      const source_lang = collection === "es" ? "es" : lang;
      const r = await api.post("/submissions", {
        ...form,
        source_lang,
        word_lang: collection,
      });
      setResult(r.data);
      toast.success(t("submit_success", lang));
      setForm({ word: "", category: "insult", region: "", definition: "", example: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main data-testid="submit-page" className="max-w-3xl mx-auto px-6 lg:px-8 py-12 sm:py-20">
      <span className="text-[11px] tracking-eyebrow text-[#E85D04] font-medium">
        Bidra · Contribute
      </span>
      <h1 className="mt-3 font-serif-display font-bold tracking-tighter text-gray-900 text-5xl sm:text-6xl">
        {t("submit_title", lang)}
      </h1>
      <p className="mt-4 text-gray-600">{t("submit_subtitle", lang)}</p>

      <form onSubmit={submit} className="mt-12 space-y-8">
        <div>
          <Label className="text-[11px] tracking-eyebrow text-gray-500">
            {t("source_label", lang)}
          </Label>
          <div
            data-testid="submit-source-switcher"
            className="mt-2 flex items-center rounded-full border border-gray-300 p-0.5 bg-white w-fit"
          >
            {[
              { code: "no", label: "🇳🇴 Norsk" },
              { code: "es", label: "🇪🇸 Español" },
            ].map((o) => (
              <button
                key={o.code}
                type="button"
                data-testid={`submit-source-${o.code}`}
                onClick={() => setCollection(o.code)}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                  collection === o.code
                    ? "bg-gray-900 text-white"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-[11px] tracking-eyebrow text-gray-500">
            {t(collection === "es" ? "field_word_es" : "field_word", lang)}
          </Label>
          <Input
            data-testid="submit-word"
            required
            value={form.word}
            onChange={(e) => update("word", e.target.value)}
            className={`mt-2 py-6 text-2xl font-serif-display bg-white border-gray-200 focus-visible:ring-[#E85D04] ${
              dupStatus === "duplicate" ? "border-red-400 focus-visible:ring-red-400" : ""
            }`}
            placeholder={collection === "es" ? "Gilipollas" : "Faen"}
          />
          {dupStatus === "checking" && (
            <p data-testid="dup-checking" className="mt-2 text-xs text-gray-400">
              {t("checking_word", lang)}
            </p>
          )}
          {dupStatus === "duplicate" && (
            <p data-testid="dup-error" className="mt-2 text-sm text-red-600 font-medium">
              {t("duplicate_word", lang)}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <Label className="text-[11px] tracking-eyebrow text-gray-500">
              {t("field_category", lang)}
            </Label>
            <Select value={form.category} onValueChange={(v) => update("category", v)}>
              <SelectTrigger
                data-testid="submit-category"
                className="mt-2 py-6 bg-white border-gray-200"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {catLabel(c, lang)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] tracking-eyebrow text-gray-500">
              {t("field_region", lang)}
            </Label>
            <Input
              data-testid="submit-region"
              value={form.region}
              onChange={(e) => update("region", e.target.value)}
              className="mt-2 py-6 bg-white border-gray-200 focus-visible:ring-[#E85D04]"
              placeholder="Oslo, Bergen…"
            />
          </div>
        </div>

        <div>
          <Label className="text-[11px] tracking-eyebrow text-gray-500">
            {t("field_definition", lang)}
          </Label>
          <Textarea
            data-testid="submit-definition"
            required
            value={form.definition}
            onChange={(e) => update("definition", e.target.value)}
            rows={3}
            className="mt-2 bg-white border-gray-200 focus-visible:ring-[#E85D04] text-base"
          />
        </div>

        <div>
          <Label className="text-[11px] tracking-eyebrow text-gray-500">
            {t("field_example", lang)}
          </Label>
          <Textarea
            data-testid="submit-example"
            required
            value={form.example}
            onChange={(e) => update("example", e.target.value)}
            rows={2}
            className="mt-2 bg-white border-gray-200 focus-visible:ring-[#E85D04] text-base"
          />
        </div>

        <div className="flex items-center gap-3 p-4 rounded-xl bg-[#F3F1ED] border border-gray-200">
          <Sparkle size={18} weight="fill" className="text-[#E85D04] flex-shrink-0" />
          <p className="text-xs sm:text-sm text-gray-600">{t("ai_translation_note", lang)}</p>
        </div>

        <Button
          type="submit"
          data-testid="submit-btn"
          disabled={loading || dupStatus === "duplicate" || dupStatus === "checking"}
          className="w-full rounded-full bg-[#E85D04] hover:bg-[#DC2F02] text-white py-6 text-base font-medium"
        >
          {loading ? t("submitting", lang) : t("submit_btn", lang)}
        </Button>
      </form>

      {result && (
        <div
          data-testid="submission-result"
          className="mt-12 p-7 bg-white border border-gray-200 rounded-2xl rise"
        >
          <span className="text-[11px] tracking-eyebrow text-[#2A9D8F] font-medium">
            ✓ {t("submit_success", lang)}
          </span>
          <h3 className="mt-3 font-serif-display text-4xl font-bold tracking-tighter">
            {result.word}
          </h3>
          <p className="mt-4 text-[11px] tracking-eyebrow text-gray-500">
            AI · {t("translations_label", lang)}
          </p>
          <div className="mt-2 space-y-2">
            {["no", "es", "en", "pl"].map((l) => (
              <div key={l} className="text-sm text-gray-700">
                <span className="font-mono-code text-[10px] uppercase text-gray-400 mr-2">
                  {l}
                </span>
                {result.translations?.[l] || "—"}
              </div>
            ))}
          </div>
        </div>
      )}
    </main>
  );
}
